/**
 * 設備資訊工具
 * 智能識別設備類型並提供相應的資訊
 */

class DeviceInfo {
  constructor() {
    this.deviceType = this.detectDeviceType();
    this.deviceInfo = this.collectDeviceInfo();
  }

  /**
   * 檢測設備類型
   * @returns {string} 設備類型
   */
  detectDeviceType() {
    const userAgent = navigator.userAgent.toLowerCase();

    if (
      /mobile|android|iphone|ipad|ipod|blackberry|windows phone/.test(userAgent)
    ) {
      return 'mobile';
    } else if (/tablet|ipad/.test(userAgent)) {
      return 'tablet';
    }
    return 'desktop';
  }

  /**
   * 獲取設備品牌
   * @returns {string} 設備品牌
   */
  getDeviceBrand() {
    const userAgent = navigator.userAgent;

    if (userAgent.includes('iPhone')) return 'Apple';
    if (userAgent.includes('iPad')) return 'Apple';
    if (userAgent.includes('Mac')) return 'Apple';
    if (userAgent.includes('Android')) {
      // 嘗試從 userAgent 中提取品牌
      const brandMatch = userAgent.match(
        /(?:^|\s)([A-Za-z]+)\s+(?:Build|MIUI|Flyme)/
      );
      if (brandMatch) return brandMatch[1];
      return 'Android';
    }
    if (userAgent.includes('Windows')) return 'Microsoft';
    if (userAgent.includes('Linux')) return 'Linux';

    return 'Unknown';
  }

  /**
   * 獲取設備版本/型號
   * @returns {string} 設備版本
   */
  getDeviceVersion() {
    const userAgent = navigator.userAgent;

    if (userAgent.includes('iPhone')) {
      const match = userAgent.match(/iPhone\s+OS\s+(\d+)_(\d+)/);
      if (match) return `iOS${match[1]}.${match[2]}`;
      return 'iPhone';
    }
    if (userAgent.includes('iPad')) {
      const match = userAgent.match(/iPad.*OS\s+(\d+)_(\d+)/);
      if (match) return `iPadOS${match[1]}.${match[2]}`;
      return 'iPad';
    }
    if (userAgent.includes('Mac')) {
      const match = userAgent.match(/Mac\s+OS\s+X\s+(\d+)[._](\d+)/);
      if (match) return `macOS${match[1]}.${match[2]}`;
      return 'macOS';
    }
    if (userAgent.includes('Android')) {
      const match = userAgent.match(/Android\s+(\d+\.\d+)/);
      if (match) return `Android${match[1]}`;
      return 'Android';
    }
    if (userAgent.includes('Windows')) {
      const match = userAgent.match(/Windows\s+NT\s+(\d+\.\d+)/);
      if (match) {
        const version = parseFloat(match[1]);
        if (version === 10.0) return 'Windows 10/11';
        if (version === 6.3) return 'Windows 8.1';
        if (version === 6.2) return 'Windows 8';
        if (version === 6.1) return 'Windows 7';
        return `Windows${version}`;
      }
      return 'Windows';
    }

    return 'Web';
  }

  /**
   * 獲取應用程式版本
   * @returns {string} 應用程式版本
   */
  getAppVersion() {
    // 可以從 package.json 或其他配置中獲取
    // 暫時使用一個預設值
    return '1.0.0';
  }

  /**
   * 獲取用戶 IP 地址
   * @returns {Promise<string>} 用戶 IP 地址
   */
  getUserIP() {
    // 由於 CSP 限制，無法使用外部 IP 服務
    // 返回本地 IP 或預設值
    return Promise.resolve('127.0.0.1');
  }

  /**
   * 生成或獲取設備 UUID
   * @returns {string} 設備 UUID
   */
  getDeviceUuid() {
    const storageKey = 'apollo_device_uuid';

    // 嘗試從 localStorage 獲取已存在的 UUID
    let uuid = localStorage.getItem(storageKey);

    if (!uuid) {
      // 生成新的 UUID
      uuid = this.generateUuid();
      // 儲存到 localStorage
      localStorage.setItem(storageKey, uuid);
    }

    return uuid;
  }

  /**
   * 生成 UUID
   * @returns {string} 生成的 UUID
   */
  generateUuid() {
    // 使用 crypto.randomUUID() 如果可用，否則使用傳統方法
    if (crypto.randomUUID) {
      return crypto.randomUUID();
    }

    // 傳統 UUID 生成方法
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
      /[xy]/g,
      function (c) {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      }
    );
  }

  /**
   * 獲取硬體資訊
   * @returns {Object} 硬體資訊
   */
  getHardwareInfo() {
    return {
      cores: navigator.hardwareConcurrency || 'unknown',
      memory: navigator.deviceMemory || 'unknown',
      platform: navigator.platform || 'unknown',
      language: navigator.language || 'unknown',
      languages: navigator.languages || [],
    };
  }

  /**
   * 收集完整的設備資訊
   * @returns {Object} 完整的設備資訊
   */
  collectDeviceInfo() {
    return {
      type: this.deviceType,
      brand: this.getDeviceBrand(),
      version: this.getDeviceVersion(),
      hardware: this.getHardwareInfo(),
      userAgent: navigator.userAgent,
      screen: {
        width: screen.width,
        height: screen.height,
        colorDepth: screen.colorDepth,
        pixelDepth: screen.pixelDepth,
      },
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
      },
    };
  }

  /**
   * 獲取 FIDO2 註冊所需的設備資訊
   * @returns {Promise<Object>} FIDO2 設備資訊
   */
  getFido2DeviceInfo() {
    return this.getUserIP().then((userIP) => {
      return {
        userId: 'user_' + this.generateUuid().substring(0, 8),
        userIp: userIP,
        appVersion: this.getAppVersion(),
        deviceType: this.deviceType,
        deviceUuid: this.getDeviceUuid(),
        deviceBrand: this.getDeviceBrand(),
        deviceVersion: this.getDeviceVersion(),
      };
    });
  }

  /**
   * 獲取設備資訊摘要
   * @returns {string} 設備資訊摘要
   */
  getDeviceSummary() {
    return `${this.deviceInfo.brand} ${this.deviceInfo.version} (${this.deviceInfo.type})`;
  }
}

// 創建單例實例
const deviceInfo = new DeviceInfo();

export default deviceInfo;



/** WEBPACK FOOTER **
 ** D:/a/1/s/src/utils/deviceInfo.js
 **/